import dayjs, { type Dayjs } from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { useMemo } from "react"
import { useSelector } from "react-redux"

import type { RootState } from "../../../../store.ts"
import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { MILESTONE_STATUSES } from "../constants/milestone-statuses.ts"

dayjs.extend(utc)
dayjs.extend(timezone)

type ParseDate = (value: unknown) => Dayjs | null

/**
 * Finished: Completed, or an actual end recorded, or progress at 100. The same
 * rule as the API's `finished_milestone_q`; nothing finished is ever late or
 * overdue.
 */
export const isMilestoneFinished = (milestone: {
  status?: number | null
  actual_end_date?: string | null
  progress?: number | string | null
}): boolean =>
  milestone.status === MILESTONE_STATUSES.COMPLETED ||
  !!milestone.actual_end_date ||
  Number(milestone.progress) >= 100

/**
 * Today as a calendar day in the account's timezone, at local midnight so it
 * compares day-for-day with the dates `parseDate` returns. Without a timezone
 * it falls back to the default `DayjsProvider` set, which is the account's too.
 */
export const accountToday = (accountTimezone?: string | null): Dayjs => {
  const now = accountTimezone ? dayjs().tz(accountTimezone) : dayjs.tz()
  return dayjs(now.format(DATE_FORMAT), DATE_FORMAT)
}

export const useAccountToday = (): Dayjs => {
  const accountTimezone = useSelector(
    (state: RootState) => state.auth?.userInfo?.timezone
  )
  return useMemo(() => accountToday(accountTimezone), [accountTimezone])
}

/**
 * Days from the actual end (or, before one is recorded, the account's today)
 * to the planned end: positive is ahead, negative is behind. `null` without a
 * planned end.
 */
export const milestoneVarianceDays = (
  milestone: { end_date?: string | null; actual_end_date?: string | null },
  today: Dayjs,
  parseDate: ParseDate
): number | null => {
  const plannedEnd = parseDate(milestone.end_date)
  if (!plannedEnd) return null
  const reference = parseDate(milestone.actual_end_date) ?? today
  return plannedEnd.startOf("day").diff(reference.startOf("day"), "day")
}

/** Finished after the planned end: both dates read in the account's format. */
export const isFinishedLate = (
  actualEnd: unknown,
  plannedEnd: unknown,
  parseDate: ParseDate
): boolean => {
  const actual = parseDate(actualEnd)
  const planned = parseDate(plannedEnd)
  return !!actual && !!planned && actual.isAfter(planned, "day")
}
